import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { FiCalendar, FiUsers, FiMapPin, FiCheck } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useBooking } from '../context/BookingContext';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const Booking = () => {
  const { apartmentId } = useParams();
  const navigate = useNavigate();
  const { bookingData, setDates, setGuests, setPricing } = useBooking();
  const { user } = useAuth();

  const [apartment, setApartment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [checkIn, setCheckIn] = useState(bookingData.checkIn);
  const [checkOut, setCheckOut] = useState(bookingData.checkOut);
  const [guests, setGuestsState] = useState(bookingData.guests || { adults: 1, children: 0 });
  const [pricing, setPricingState] = useState(bookingData.pricing);
  const [guestInfo, setGuestInfoState] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || ''
  });
  const [specialRequests, setSpecialRequests] = useState('');

  useEffect(() => {
    const fetchApartment = async () => {
      try {
        const data = await api.getApartmentById(apartmentId);
        setApartment(data);
      } catch (error) {
        toast.error('Apartment not found');
        navigate('/apartments');
      } finally {
        setLoading(false);
      }
    };
    fetchApartment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apartmentId]);

  useEffect(() => {
    const calculatePrice = async () => {
      if (!apartment || !checkIn || !checkOut) return;

      try {
        const data = await api.calculatePrice({
          apartmentId: apartment._id,
          checkIn: checkIn.toISOString(),
          checkOut: checkOut.toISOString(),
          guests
        });
        setPricingState(data);
        setPricing(data);
      } catch (error) {
        console.error('Price calculation error:', error);
      }
    };
    calculatePrice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apartment, checkIn, checkOut, guests]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!checkIn || !checkOut) {
      toast.error('Please select check-in and check-out dates');
      return;
    }

    if (!guestInfo.firstName || !guestInfo.lastName || !guestInfo.email || !guestInfo.phone) {
      toast.error('Please fill in all guest information');
      return;
    }

    setSubmitting(true);

    try {
      const bookingPayload = {
        apartmentId: apartment._id,
        checkIn: checkIn.toISOString(),
        checkOut: checkOut.toISOString(),
        guests,
        guestInfo,
        specialRequests
      };

      const result = await api.createBooking(bookingPayload);

      // Create payment session
      const paymentSession = await api.createCheckoutSession(result.booking.id);

      // Redirect to Stripe checkout
      if (paymentSession.url) {
        window.location.href = paymentSession.url;
      } else {
        toast.error('Payment setup failed');
      }
    } catch (error) {
      toast.error(error.message || 'Booking failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading-container"><div className="spinner" /></div>;
  }

  if (!apartment) return null;

  const styles = {
    container: { maxWidth: '1100px', margin: '0 auto', padding: '40px 24px' },
    header: { marginBottom: '32px' },
    title: { fontSize: '1.75rem', fontWeight: '700', marginBottom: '8px' },
    grid: { display: 'grid', gridTemplateColumns: '1fr 400px', gap: '48px' },
    section: { background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px', border: '1px solid #eee' },
    sectionTitle: { fontSize: '1.125rem', fontWeight: '600', marginBottom: '20px' },
    apartmentCard: { display: 'flex', gap: '16px', marginBottom: '24px' },
    apartmentImage: { width: '120px', height: '90px', borderRadius: '8px', objectFit: 'cover' },
    inputGroup: { marginBottom: '20px' },
    label: { display: 'block', fontWeight: '500', marginBottom: '8px' },
    input: { width: '100%', padding: '12px 16px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem' },
    dateGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
    summaryCard: { position: 'sticky', top: '100px', background: 'white', border: '1px solid #ddd', borderRadius: '12px', padding: '24px' },
    summaryRow: { display: 'flex', justifyContent: 'space-between', marginBottom: '12px' },
    summaryTotal: { display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '1.125rem', paddingTop: '16px', borderTop: '1px solid #eee', marginTop: '16px' },
    submitBtn: { width: '100%', padding: '16px', background: '#1a3a5c', color: 'white', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', marginTop: '24px' }
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Complete your booking</h1>
      </header>

      <form onSubmit={handleSubmit}>
        <div style={styles.grid} className="booking-grid">
          <div>
            {/* Apartment Info */}
            <div style={styles.section}>
              <div style={styles.apartmentCard}>
                <img src={apartment.images?.[0] || '/images/placeholder-apartment.jpg'} alt={apartment.name} style={styles.apartmentImage} />
                <div>
                  <h3 style={{ fontWeight: '600', marginBottom: '4px' }}>{apartment.name}</h3>
                  <p style={{ color: '#555', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <FiMapPin /> {apartment.address?.city}
                  </p>
                </div>
              </div>
            </div>

            {/* Dates & Guests */}
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>Your trip</h2>
              <div style={styles.dateGrid}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}><FiCalendar style={{ marginRight: '8px' }} />Check-in</label>
                  <DatePicker
                    selected={checkIn}
                    onChange={(date) => { setCheckIn(date); setDates(date, checkOut); }}
                    selectsStart
                    startDate={checkIn}
                    endDate={checkOut}
                    minDate={new Date()}
                    dateFormat="dd MMM yyyy"
                    className="form-control"
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}><FiCalendar style={{ marginRight: '8px' }} />Check-out</label>
                  <DatePicker
                    selected={checkOut}
                    onChange={(date) => { setCheckOut(date); setDates(checkIn, date); }}
                    selectsEnd
                    startDate={checkIn}
                    endDate={checkOut}
                    minDate={checkIn || new Date()}
                    dateFormat="dd MMM yyyy"
                    className="form-control"
                  />
                </div>
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}><FiUsers style={{ marginRight: '8px' }} />Guests</label>
                <select
                  style={styles.input}
                  value={guests.adults}
                  onChange={(e) => { const g = { ...guests, adults: parseInt(e.target.value) }; setGuestsState(g); setGuests(g); }}
                >
                  {Array.from({ length: apartment.maxGuests }, (_, i) => i + 1).map(n => (
                    <option key={n} value={n}>{n} guest{n > 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Guest Info */}
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>Guest information</h2>
              <div style={styles.dateGrid}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>First name *</label>
                  <input type="text" style={styles.input} value={guestInfo.firstName} onChange={(e) => setGuestInfoState({ ...guestInfo, firstName: e.target.value })} required />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Last name *</label>
                  <input type="text" style={styles.input} value={guestInfo.lastName} onChange={(e) => setGuestInfoState({ ...guestInfo, lastName: e.target.value })} required />
                </div>
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Email *</label>
                <input type="email" style={styles.input} value={guestInfo.email} onChange={(e) => setGuestInfoState({ ...guestInfo, email: e.target.value })} required />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Phone *</label>
                <input type="tel" style={styles.input} value={guestInfo.phone} onChange={(e) => setGuestInfoState({ ...guestInfo, phone: e.target.value })} required />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Special requests (optional)</label>
                <textarea style={{ ...styles.input, minHeight: '100px' }} value={specialRequests} onChange={(e) => setSpecialRequests(e.target.value)} placeholder="Any special requirements?" />
              </div>
            </div>
          </div>

          {/* Booking Summary */}
          <div>
            <div style={styles.summaryCard}>
              <h3 style={{ fontWeight: '600', marginBottom: '20px' }}>Price breakdown</h3>

              {pricing ? (
                <>
                  <div style={styles.summaryRow}>
                    <span>£{apartment.pricePerNight} x {pricing.nights} nights</span>
                    <span>£{pricing.basePrice.toFixed(2)}</span>
                  </div>
                  {pricing.discount > 0 && (
                    <div style={{ ...styles.summaryRow, color: '#008A05' }}>
                      <span>Discount</span>
                      <span>-£{pricing.discount.toFixed(2)}</span>
                    </div>
                  )}
                  {pricing.cleaningFee > 0 && (
                    <div style={styles.summaryRow}>
                      <span>Cleaning fee</span>
                      <span>£{pricing.cleaningFee.toFixed(2)}</span>
                    </div>
                  )}
                  <div style={styles.summaryRow}>
                    <span>Service fee</span>
                    <span>£{pricing.serviceFee.toFixed(2)}</span>
                  </div>
                  <div style={styles.summaryTotal}>
                    <span>Total</span>
                    <span>£{pricing.totalPrice.toFixed(2)}</span>
                  </div>
                </>
              ) : (
                <p style={{ color: '#555' }}>Select dates to see pricing</p>
              )}

              <button type="submit" style={styles.submitBtn} disabled={submitting || !pricing}>
                {submitting ? 'Processing...' : 'Confirm and pay'}
              </button>

              <p style={{ fontSize: '0.75rem', color: '#555', textAlign: 'center', marginTop: '16px' }}>
                <FiCheck style={{ marginRight: '4px' }} />
                Free cancellation within 24 hours of booking
              </p>
            </div>
          </div>
        </div>
      </form>

      <style>{`
        @media (max-width: 992px) {
          .booking-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default Booking;
